import { FilterOption } from "../filterPopover/FilterPopover.types";
import { FilterOperatorUi, ListFilter, ListFilterOperator, TextFilter, TextFilterOperator } from "@common/api/filterTypes";
import { hasFilterValue } from "./filterPinning";

// How a filter option's value becomes a property on the search request. The option type alone does not say
// whether a list carries ids or enum numbers, so each page declares the kind beside the property name.
export enum FilterPropertyKind {
    Boolean,
    Text,
    IdList,
    NumericList,
    DateRange,
    NumberRange,
}

export interface FilterPropertyMapping<TProperty extends string = string> {
    property: TProperty;
    kind: FilterPropertyKind;
}

export function filterIdToBackendProperty(mappings: Record<string, FilterPropertyMapping>): Record<string, string> {
    return Object.fromEntries(Object.entries(mappings).map(([filterId, mapping]) => [filterId, mapping.property]));
}

// Fills the request properties the options carry a value for; an option without a value leaves its property
// absent, which the backend reads as "unset".
export function mapFilterOptionsToRequest<TRequest>(filterOptions: Array<FilterOption>, mappings: Record<string, FilterPropertyMapping>): Partial<TRequest> {
    const result: Record<string, unknown> = {};

    filterOptions.forEach((option) => {
        const mapping = mappings[option.id];
        if (!mapping || !hasFilterValue(option.value)) {
            return;
        }

        const value = toRequestValue(option, mapping.kind);
        if (value !== undefined) {
            result[mapping.property] = value;
        }
    });

    return result as Partial<TRequest>;
}

function toRequestValue(option: FilterOption, kind: FilterPropertyKind): unknown {
    switch (kind) {
        case FilterPropertyKind.Boolean:
            return extractBoolean(option.value);
        case FilterPropertyKind.Text:
            return toTextFilter(option);
        case FilterPropertyKind.IdList:
            return toListFilterOrStored(option, (values) => values);
        case FilterPropertyKind.NumericList:
            return toListFilterOrStored(option, (values) => values.map(Number));
        case FilterPropertyKind.DateRange:
        case FilterPropertyKind.NumberRange:
            return isRange(option.value) ? option.value : undefined;
    }
}

// A text option's value is the typed string until the operator menu wraps it; hydrateFilterOperators unwraps a
// stored TextFilter back to the string, so both shapes arrive here.
export function toTextFilter(option: FilterOption): TextFilter | undefined {
    if (typeof option.value === "string") {
        return {
            value: option.value,
            filterOperator: option.filterOperator === FilterOperatorUi.Negative ? TextFilterOperator.NotContains : TextFilterOperator.Contains,
        };
    }

    return isOperatorFilter(option.value) ? (option.value as TextFilter) : undefined;
}

// A list option's value is the picked ids, or the whole filter again when it came back from a saved view.
function toListFilterOrStored(option: FilterOption, toValues: (values: Array<any>) => Array<string | number>): ListFilter<string | number> | undefined {
    if (Array.isArray(option.value)) {
        return toListFilter(option, toValues(option.value));
    }

    return isOperatorFilter(option.value) ? (option.value as ListFilter<string | number>) : undefined;
}

export function toListFilter(option: FilterOption, values: Array<string | number>): ListFilter<string | number> {
    return {
        value: values,
        filterOperator: option.filterOperator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In,
    };
}

// The Boolean options hold a plain bool, or a one-element array when they came back from a chip; either way the
// request carries a bool the backend can treat as "unset when absent".
export function extractBoolean(value: any): boolean | undefined {
    const selected = Array.isArray(value) ? value[0] : value;
    if (selected === undefined || selected === "") {
        return undefined;
    }

    return selected === true || selected === "true";
}

export function isOperatorFilter(value: unknown): boolean {
    return typeof value === "object" && value !== null && "value" in value && "filterOperator" in value;
}

export function isRange(value: any): boolean {
    return typeof value === "object" && ("from" in value || "to" in value || "dateFieldEnum" in value || "rollingPeriod" in value);
}

// The value a stored filter shows in its option: the plain value under an operator filter, with list members as
// strings the way the pickers hold them, or the stored shape itself when there is no operator around it.
export function extractFilterValue(filter: any): any {
    if (!filter) {
        return "";
    }

    if (filter.value !== undefined && filter.filterOperator !== undefined) {
        return Array.isArray(filter.value) ? filter.value.map((item: string | number) => item.toString()) : filter.value;
    }

    return filter;
}
